import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageTitle } from "@/hooks/usePageTitle";

const safetyItems = [
  {
    icon: "ri-article-line",
    title: "作業前リスクアセスメント",
    description:
      "全作業前にリスクアセスメントを実施。気象・海象条件、作業環境、体力状態などを総合的に評価し、安全な作業計画を立案します。",
  },
  {
    icon: "ri-graduation-cap-line",
    title: "教育・研修制度",
    description:
      "入社時の基礎研修から、現場でのOJTまで段階的な教育プログラムを整備。安全意識の醸成を重視しています。",
  },
  {
    icon: "ri-first-aid-kit-line",
    title: "救急・医療提携体制",
    description:
      "全現場に救急・医療体制を示した掲示板があります。緊急時体制表を確認して緊急での対応が可能です。",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "健康診断・高気圧検診",
    description:
      "潜水士は年1回の健康診断に加えて、年2回一般の健康診断とは別で高気圧検診の受診が義務付けられています。",
  },
  {
    icon: "ri-tools-line",
    title: "設備・器材管理",
    description:
      "潜水器材は使用ごとに点検・整備を実施。法定点検に加え、自社基準による徹底したメンテナンスを行っています。",
  },
];

const stats = [
  { value: "0", label: "創業から現在までの重大事故", unit: "件" },
  { value: "100", label: "安全研修実施率", unit: "%" },
  { value: "24", label: "緊急対応体制", unit: "h" },
];

export default function SafetyPage() {
  usePageTitle("安全管理体制");
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-ocean-800">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-widest text-ocean-300 border border-ocean-500 rounded-full mb-4">
                SAFETY FIRST
              </span>
              <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4 max-w-2xl">
                安全管理体制
              </h1>
              <p className="text-white/60 max-w-xl text-sm md:text-base">
                潜水作業は常に危険と隣り合わせ。徹底した安全管理こそが最高の品質を生み出します。
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-ocean-700">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl md:text-6xl font-bold text-ocean-300 font-serif">
                      {stat.value}
                    </span>
                    <span className="text-xl font-bold text-ocean-300">
                      {stat.unit}
                    </span>
                  </div>
                  <p className="text-sm text-white/60">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-ocean-50">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-serif text-2xl md:text-3xl font-bold text-ocean-800 mb-4">
                  5つの安全管理の柱
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12 items-stretch">
                <div className="rounded-2xl overflow-hidden h-full flex flex-col">
                  <div className="w-full flex-1 min-h-56">
                    <img
                      src="https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/d8860deff81d9e7a746b7109e20f5554.jpeg"
                      alt="潜水訓練中の様子"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="bg-ocean-800 px-5 py-3">
                    <p className="text-white/70 text-xs">定期的な潜水訓練で技術と安全意識を維持。入水前の器材確認・コミュニケーション訓練も実施。</p>
                  </div>
                </div>
                <div className="flex flex-col gap-4 h-full">
                  <div className="bg-white rounded-xl p-5 border border-ocean-100 flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-ocean-50 text-ocean-500">
                        <i className="ri-graduation-cap-line text-lg" />
                      </div>
                      <h3 className="text-2xl font-bold text-ocean-800 mb-4">定期訓練の内容</h3>
                    </div>
                    <div className="space-y-4 pl-0">
                      {["入水前機材チェック・確認", "緊急浮上・救急訓練", "水中コミュニケーション訓練", "体力・体制維持訓練"].map((item) => (
                        <div key={item} className="flex items-start gap-2 text-xl text-ocean-700">
                          <span>・{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

              <div className="flex flex-col gap-5">
                {safetyItems.map((item) => (
                  <div
                    key={item.title}
                    className="bg-white rounded-xl p-5 md:p-6 border border-ocean-100 flex items-start gap-5"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-ocean-50 text-ocean-500 shrink-0">
                      <i className={`${item.icon} text-xl`} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-ocean-800 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-ocean-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}